import React from "react";
import styles from "./Contact_Page.module.css";
import Input from "../components/ui/Input";
import Button from "../components/ui/Button";
import email_image from "../assets/icons_contactuspage/Email.svg";
import demo_image from "../assets/icons_contactuspage/Demo.svg";
import question_image from "../assets/icons_contactuspage/question.svg";
import Alert from "@mui/material/Alert";

const API_URL = process.env.REACT_APP_API_URL_CONTACT; // AWS Lambda endpoint
const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY; // v2 checkbox

const Contact = () => {
  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const recaptchaRef = React.useRef(null);
  const [widgetId, setWidgetId] = React.useState(null);

  // ---- Load reCAPTCHA v2 ONCE ----
  // React.useEffect(() => {
  //   document
  //     .querySelectorAll('script[src*="recaptcha/api.js?render="]')
  //     .forEach((s) => s.remove());

  //   // Inject v2 script once
  //   if (!document.querySelector('script[data-recaptcha="v2-auto"]')) {
  //     const s = document.createElement("script");
  //     s.src = "https://www.google.com/recaptcha/api.js"; // auto-render for .g-recaptcha
  //     s.async = true;
  //     s.defer = true;
  //     s.dataset.recaptcha = "v2-auto";
  //     document.head.appendChild(s);
  //   }
  // }, []);
  React.useEffect(() => {
    let cancelled = false;

    const ensureScript = () =>
      new Promise((resolve) => {
        if (!SITE_KEY_V2) return resolve();
        if (window.grecaptcha?.render) return resolve();
        const existing = document.querySelector(
          'script[data-recaptcha="v2-auto"]'
        );
        if (existing) {
          const check = () =>
            window.grecaptcha?.render ? resolve() : setTimeout(check, 50);
          check();
          return;
        }
        const s = document.createElement("script");
        s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        s.async = true;
        s.defer = true;
        s.dataset.recaptcha = "v2-auto";
        s.onload = resolve;
        document.head.appendChild(s);
      });

    const renderWidget = async () => {
      await ensureScript();
      if (!SITE_KEY_V2) {
        console.warn(
          "reCAPTCHA site key not configured (REACT_APP_RECAPTCHA_SITE_KEY) - skipping widget render"
        );
        return;
      }
      if (cancelled || !recaptchaRef.current) return;
      recaptchaRef.current.innerHTML = ""; // clear any stale iframes when navigating back
      let id;
      try {
        id = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: SITE_KEY_V2,
          theme: "light",
          size: "normal",
        });
      } catch (err) {
        console.error("reCAPTCHA render failed:", err);
        return;
      }
      if (!cancelled) setWidgetId(id);
    };

    renderWidget();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Validation helpers ----
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "firstName":
      case "lastName":
        if (!/^[A-Za-z\s-']+$/.test(value)) {
          error =
            "Please enter valid name (letters, spaces, hyphens, and apostrophes only)";
        }
        break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Please enter a valid email address";
        }
        break;
      case "phone":
        if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(value)) {
          error = "Please enter a valid 10-digit phone number";
        }
        break;
      case "message":
        if (value.length < 10) {
          error = "Message must be at least 10 characters long";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // sanitize
    let processedValue = value;
    if (name === "firstName" || name === "lastName") {
      processedValue = value.replace(/[^A-Za-z\s-']/g, "");
    } else if (name === "phone") {
      processedValue = value.replace(/[^0-9().\-\s]/g, "");
    }

    const error = validateField(name, processedValue);

    // native tooltip message
    if (typeof e.target.setCustomValidity === "function") {
      e.target.setCustomValidity(error || "");
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitted(false);
    setSubmitError("");

    if (submitting) return;

    if (!SITE_KEY_V2) {
      setSubmitError(
        "reCAPTCHA is not configured for this environment. Please contact the site administrator."
      );
      return;
    }

    // field validation
    let formValid = true;
    const newErrors = {};
    Object.keys(formData).forEach((field) => {
      if (field === "company") return;
      const err = validateField(field, formData[field]);
      if (err) {
        formValid = false;
        newErrors[field] = err;
      }
    });
    setErrors(newErrors);
    if (!formValid) {
      e.currentTarget.reportValidity?.();
      return;
    }

    // require v2 checkbox token (auto-rendered widget)
    const token =
      widgetId != null ? window.grecaptcha.getResponse(widgetId) : "";
    if (!token) {
      setSubmitError("Please check the reCAPTCHA box to continue.");
      setSubmitted(false);
      document
        .querySelector(".g-recaptcha")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    try {
      // save locally
      const submission = {
        ...formData,
        submittedAt: new Date().toISOString(),
        id: Date.now(),
      };
      const existing = JSON.parse(
        localStorage.getItem("contactSubmissions") || "[]"
      );
      existing.push(submission);
      localStorage.setItem("contactSubmissions", JSON.stringify(existing));

      // send to API
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          pageUri: window.location.href,
          pageName: document.title,
          captchaToken: token,
          captchaProvider: "v2_checkbox",
        }),
      });
      // Try to parse JSON; fall back to text; allow empty body
      let payload;
      const ct = resp.headers.get("content-type") || "";
      try {
        payload = ct.includes("application/json")
          ? await resp.json()
          : await resp.text();
      } catch {
        payload = null; // non-JSON/empty body
      }

      // If HTTP is not ok, surface a helpful error
      if (!resp.ok) {
        const detail =
          typeof payload === "string" ? payload : JSON.stringify(payload);
        throw new Error(
          `HTTP ${resp.status} ${resp.statusText}${
            detail ? ` — ${detail}` : ""
          }`
        );
      }

      // reset form + captcha
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });
      if (widgetId != null) window.grecaptcha.reset(widgetId);
      setSubmitError("");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error("Error submitting form:", err);
      setSubmitted(false);
      setSubmitError(
        "There was an error submitting your information. Please try again."
      );
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles["contact-page"]}>
      <title>Contact iCredentialing | Healthcare Provider Credentialing & Support</title>
      <meta name="description" content="Get in touch with iCredentialing for provider credentialing, onboarding, compliance support, or demo requests. Our team is available by phone or email to help."/>
      <meta
          name="keywords"
          content="credentialing, provider onboarding, healthcare compliance, automated credentialing"
      />
      <link
          rel="canonical"
          href={`https://icredentialing.com/contact`}
      />
      <div className={styles["contact-hero"]}>
        <div className={styles.container}>
          <h1>
            Got Questions? We are <span className={styles.underline}>Here</span>{" "}
            to Help.
          </h1>
          <p>
            We value our customers! Please reach out to us for any assistance.
            You can call us from Monday to Friday 9AM to 7PM (PST) at (949)
            390-2661. We are also available 24 x 7 through email.
          </p>
        </div>
      </div>

      <div className={styles["container"]}>
        {submitted && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Thank you for contacting us! Our team will reach out shortly.
          </Alert>
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}
        <div className={styles["side-by-side-container"]}>
          {/* Left side - Contact options */}
          <div className={styles["contact-options"]}>
            <div className={styles["contact-option"]}>
              <div className={styles["option-icon"]}>
                <img
                  src={question_image}
                  alt="questionimage"
                  className={styles.subHeaderIcon}
                />
              </div>
              <div className={styles["option-content"]}>
                <h3>For Questions or Service Enquiries :</h3>
                {/* fix href */}
                <p>
                  Please reach out through the <b>Contact Us</b> Form.
                </p>
              </div>
            </div>

            <div className={styles["contact-option"]}>
              <div className={styles["option-icon"]}>
                <img
                  src={email_image}
                  alt="emailimage"
                  className={styles.subHeaderIcon}
                />
              </div>
              <div className={styles["option-content"]}>
                <h3>For Customer Support :</h3>
                <p>
                  Please Email Us at{" "}
                  <a href="mailto:info@ihsconsultants.net">
                    info@ihsconsultants.net
                  </a>
                </p>
              </div>
            </div>

            <div className={styles["contact-option"]}>
              <div className={styles["option-icon"]}>
                <img
                  src={demo_image}
                  alt="demoimage"
                  className={styles.subHeaderIcon}
                />
              </div>
              <div className={styles["option-content"]}>
                <h3>For Demo Requests:</h3>
                <p>
                  Please setup an appointment via{" "}
                  <a href="/schedule-demo">Schedule A Meet</a>.
                </p>
              </div>
            </div>
          </div>

          {/* Right side - Contact form */}
          <div className={styles["contact-form-section"]} id="contact-form">
            <h4 className={styles["contact-us-heading"]}>CONTACT US</h4>
            <div className={styles["contact-form-container"]}>
              <form className={styles["contact-form"]} onSubmit={handleSubmit}>
                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <Input
                      label="First Name"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      error={errors.firstName}
                    />
                  </div>
                  <div className={styles["input-wrapper"]}>
                    <Input
                      label="Last Name"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      error={errors.lastName}
                    />
                  </div>
                </div>

                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <Input
                      label="Email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      error={errors.email}
                    />
                  </div>
                  <div className={styles["input-wrapper"]}>
                    <Input
                      label="Phone Number"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      error={errors.phone}
                    />
                  </div>
                </div>

                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <Input
                      label="Company Name"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <Input
                      label="Your Message"
                      type="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      error={errors.message}
                    />
                  </div>
                </div>

                {/* reCAPTCHA v2 checkbox (AUTO-RENDERED) */}
                <div className={styles.recaptchaWrap}>
                  {SITE_KEY_V2 ? (
                    <div ref={recaptchaRef} />
                  ) : (
                    <div className={styles.recaptchaNotice} role="status">
                      <strong>reCAPTCHA not configured</strong>
                      <p>
                        The CAPTCHA required to submit this form is not
                        configured for this environment. Please contact the
                        site administrator or try again later.
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  variant="primary"
                  type="submit"
                  className={styles["submit-button"]}
                  disabled={!SITE_KEY_V2 || submitting}
                >
                  Submit Request
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.subcontainer}>
        {/* <h2 className={styles.subheading}>
          Want to access exclusive content, expert tips and insights?
        </h2>
        <div className={styles.headline}>
          SIGN UP FOR OUR NEWSLETTER . <em>NOW</em> .
        </div>
        <form className={styles["form-newsletter"]}>
          <input
            type="email"
            placeholder="youremail@example.com"
            required
            className={styles.inputnewsletter}
          />
          <button type="submit" className={styles.buttonnewsletter}>
            Submit
          </button>
        </form> */}
      </div>
    </div>
  );
};

export default Contact;
